<template>
  <section class="m-t-60 is-flex is-align-items-center is-justify-content-space-between p-b-70">
    <div>
      <ButtonView
        v-if="!hideBack"
        class="button is-primary is-size-5-mobile"
        :data-e2e="FormCypressAttribute.buttons.FLOW_BACK"
        @click="back"
        outlined
      >
        <slot name="back">{{ $t('core.back') }}</slot>
      </ButtonView>
      <slot v-if="hideBack" name="left-side-text" />
    </div>
    <div class="is-flex is-align-items-center is-vcentered is-mobile is-size-5-mobile m-l-4">
      <div v-if="showAddAnother">
        <ButtonView
          class="m-r-5"
          :disabled="isNextAndDoneDisabled"
          type="is-primary"
          @click="nextAndDone"
          :loading="isLoading"
          outlined
          :data-e2e="FormCypressAttribute.buttons.FLOW_NEXT_AND_DONE"
        >
          <slot name="next-and-done">{{ $t('core.save-and-add-another') }}</slot>
        </ButtonView>
      </div>
      <div class="is-flex is-align-items-center m-r-12 p-r-0 is-hidden-touch">
        <p
          v-if="showPressEnterText"
          :key="renderKey"
          class="delayed-show-bu is-size-5-mobile is-flex has-gap-4"
          :data-e2e="FormCypressAttribute.texts.PRESS_ENTER_TEXT"
        >
          <span :class="pressEnterSpanClasses">
            {{ $t('core.press') }}
          </span>
          <span :class="pressEnterSpanClasses">
            {{ $t('core.onboarding-enter') }}
          </span>
        </p>
      </div>
      <div class="is-flex">
        <TooltipView
          v-if="!hideNext"
          :type="tooltipType"
          :label="tooltipLabel"
          :always="tooltipAlways"
          :active="tooltipActive"
          :position="tooltipPosition"
          :multilined="tooltipMultilined"
          :content-class="tooltipContentClass"
        >
          <ButtonView
            :disabled="nextDisabled"
            class="button is-primary is-size-5-mobile"
            :data-e2e="FormCypressAttribute.buttons.FLOW_NEXT"
            @click="next"
            :loading="isLoading"
          >
            <slot name="next">{{ $t('guidancestep.save-and-continue') }}</slot>
          </ButtonView>
        </TooltipView>
        <slot v-if="!hideNext" name="next-trailing" />
        <slot v-if="hideNext" name="right-side-text" />
      </div>
    </div>
  </section>
</template>

<script>
import DeviceService from '/@/modules/core/ui/services/DeviceService';
import KeyboardAdapter from '/@/modules/core/ui/services/KeyboardService';
import { KeyboardEvent } from '/@/modules/core/ui/ui.constants';
import { FormCypressAttribute } from '/@/modules/core/form/constants/FormCypressAttribute';
import { FormInputTooltipMixin } from '/@/modules/core/form/mixins/FormInputTooltipMixin';
import ButtonView from '@/modules/core/ui/components/wrappers/ButtonView.vue';
import TooltipView from '@/modules/core/ui/components/wrappers/TooltipView.vue';
import { FormEvent } from '../constants/FormEvent';
import { HasForceReRenderMixin } from '@/modules/core/app/mixins/HasForceReRenderMixin';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'BaseFlowControlButtons',
  emits: ['back', 'next', 'next-and-done'],
  slotsDefinition: {
    back: {},
    leftSideText: {},
    nextAndDone: {},
    next: {},
    nextTrailing: {},
    rightSideText: {}
  },
  mixins: [FormInputTooltipMixin, HasForceReRenderMixin],
  components: { TooltipView, ButtonView },
  props: {
    nextDisabled: {
      type: Boolean,
      default: false
    },
    nextAndDoneDisabled: {
      type: Boolean,
      default: null
    },
    hideNext: {
      type: Boolean,
      default: false
    },
    hideBack: {
      type: Boolean,
      default: false
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    showAddAnother: {
      type: Boolean,
      default: false
    },
    showPressEnter: {
      type: Boolean,
      default: true
    },
    disableEnter: {
      type: Boolean,
      default: false
    },
    pressEnterCustomClasses: {
      type: String,
      default: ''
    }
  },
  static: {
    FormCypressAttribute
  },
  events() {
    return {
      [FormEvent.ON_INPUT_FOCUS]: this.updateShowPressEnterText,
      [FormEvent.ON_INPUT_BLUR]: this.updateShowPressEnterText
    };
  },
  data() {
    return {
      overrideShowPressEnterText: 0
    };
  },
  computed: {
    isNextAndDoneDisabled() {
      return this.nextAndDoneDisabled !== null ? this.nextAndDoneDisabled : this.nextDisabled;
    },
    showPressEnterText() {
      // This is used to determine if the "Press Enter" text should be shown.
      // needs to be in scope to be reactive to changes in the component.
      // tried as a method, wasnt working
      this.renderKey;

      return (
        this.showPressEnter &&
        !this.hideNext &&
        !this.nextDisabled &&
        !this.showAddAnother &&
        !this.disableEnter &&
        !this.isTextAreaFocused()
      );
    },
    pressEnterSpanClasses() {
      return ['has-text-weight-semibold', 'is-size-6', this.pressEnterCustomClasses].filter(
        Boolean
      );
    }
  },
  methods: {
    updateShowPressEnterText(component) {
      if (component.type !== 'textarea') {
        return;
      }

      this.reRender();
    },
    back() {
      this.$emit('back');
    },
    next() {
      if (
        this.nextDisabled ||
        this.hideNext ||
        this.isMultiSelectOpen() ||
        this.isAutocompleteOpen() ||
        this.disableEnter ||
        this.isTextAreaFocused()
      ) {
        return;
      }

      this.$emit('next');
    },
    nextAndDone() {
      if (this.isNextAndDoneDisabled || this.isMultiSelectOpen() || this.isAutocompleteOpen()) {
        return;
      }

      this.$emit('next-and-done');
    },
    isMultiSelectOpen() {
      return document.getElementsByClassName('multiselect--active').length > 0;
    },
    isAutocompleteOpen() {
      return document.querySelector('.autocomplete .dropdown-menu')?.style.display === '';
    },
    isTextAreaFocused() {
      return !!document.querySelectorAll('textarea:focus').length;
    }
  },
  created() {
    if (!DeviceService.isMobile()) {
      KeyboardAdapter.on(KeyboardEvent.ENTER, this.next);
    }
  },
  beforeUnmount() {
    if (!DeviceService.isMobile()) {
      KeyboardAdapter.off(KeyboardEvent.ENTER, this.next);
    }
  }
};
</script>
